// v2/config/permissions.config.ts — Permission matrices as structured data
// Extracted from v1: features/Permission/permissions.ts

import { Action, ApplicationState, ProductType, Role } from '../shared/types/domain';
import { DefaultPermissions, PermissionMatrix } from './types';

// ─── Consumer Durables Loan Permissions ──────────────────────────────────────

const CONSUMER_DURABLES_LOAN_PERMISSIONS: PermissionMatrix = {
  [Role.SALES_USER]: {
    [ApplicationState.DRAFT]: [
      Action.CREATE_APPLICATION,
      Action.EDIT_APPLICATION,
      Action.SAVE_APPLICATION,
      Action.SUBMIT_APPLICATION,
    ],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.UPLOAD_DOCUMENT],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.INVOICE_COLLECTED],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.BACK_OFFICE_USER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.SANCTION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.SALES_MANAGER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
  [Role.ADMIN]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
};

// ─── Personal Loan Permissions ───────────────────────────────────────────────

const PERSONAL_LOAN_PERMISSIONS: PermissionMatrix = {
  [Role.SALES_USER]: {
    [ApplicationState.DRAFT]: [
      Action.CREATE_APPLICATION,
      Action.EDIT_APPLICATION,
      Action.SAVE_APPLICATION,
      Action.SUBMIT_APPLICATION,
    ],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.UPLOAD_DOCUMENT],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.BACK_OFFICE_USER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.START_VERIFICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION, Action.SANCTION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.MANDATE],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.SALES_MANAGER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
  [Role.ADMIN]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
};

// ─── Business Loan Permissions ───────────────────────────────────────────────

const BUSINESS_LOAN_PERMISSIONS: PermissionMatrix = {
  [Role.SALES_USER]: {
    [ApplicationState.DRAFT]: [
      Action.CREATE_APPLICATION,
      Action.EDIT_APPLICATION,
      Action.SAVE_APPLICATION,
      Action.SUBMIT_APPLICATION,
    ],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.UPLOAD_DOCUMENT],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.BACK_OFFICE_USER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.START_VERIFICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION, Action.SANCTION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.MANDATE],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.SALES_MANAGER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
  [Role.ADMIN]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
};

// ─── Group Loan Permissions ──────────────────────────────────────────────────

const GROUP_LOAN_PERMISSIONS: PermissionMatrix = {
  [Role.SALES_USER]: {
    [ApplicationState.DRAFT]: [
      Action.CREATE_APPLICATION,
      Action.EDIT_APPLICATION,
      Action.SAVE_APPLICATION,
      Action.SUBMIT_APPLICATION,
    ],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.UPLOAD_DOCUMENT],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.BACK_OFFICE_USER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.START_VERIFICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION, Action.SANCTION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.MANDATE],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.SALES_MANAGER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
  [Role.ADMIN]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
  },
};

// ─── Product-Type Permission Map ─────────────────────────────────────────────

export const PRODUCT_TYPE_PERMISSIONS: Record<ProductType, PermissionMatrix> = {
  [ProductType.PERSONAL_LOAN]: PERSONAL_LOAN_PERMISSIONS,
  [ProductType.BUSINESS_LOAN]: BUSINESS_LOAN_PERMISSIONS,
  [ProductType.CONSUMER_DURABLES_LOAN]: CONSUMER_DURABLES_LOAN_PERMISSIONS,
  [ProductType.GROUP_LOAN]: GROUP_LOAN_PERMISSIONS,
};

// ─── Default Role-Based Permissions (no application state context) ───────────

export const DEFAULT_ROLE_PERMISSIONS: DefaultPermissions = {
  [Role.SALES_USER]: [
    Action.CREATE_APPLICATION,
    Action.EDIT_APPLICATION,
    Action.SUBMIT_APPLICATION,
    Action.INVOICE_COLLECTED,
  ],
  [Role.BACK_OFFICE_USER]: [
    Action.SOFT_APPROVE,
    Action.REQUEST_EDIT_APPLICATION,
    Action.SANCTION,
    Action.MANDATE,
    Action.SIGN,
    Action.DISBURSE,
  ],
  [Role.FIELD_USER]: [],
  [Role.SALES_MANAGER]: [Action.VIEW_USER_LOGIN_HISTORY],
  [Role.ADMIN]: [Action.VIEW_USER_LOGIN_HISTORY],
};